import React, { useState, useEffect, useMemo, useRef } from 'react';
import axios from 'axios';

const API_URL = process.env.NODE_ENV === 'production' 
  ? '/api' 
  : (process.env.REACT_APP_API_URL || 'http://localhost:3001/api');

const CLI_TOOL_OPTIONS = ['Cursor', 'Claude'];

const MODEL_OPTIONS = {
  Claude: [
    { value: 'claude-3-5-sonnet-20241022', label: 'Claude 3.5 Sonnet (Oct 2024)' },
    { value: 'claude-3-5-sonnet-20240620', label: 'Claude 3.5 Sonnet (Jun 2024)' },
    { value: 'claude-3-5-haiku-20241022', label: 'Claude 3.5 Haiku (Oct 2024)' },
    { value: 'claude-3-opus-20240229', label: 'Claude 3 Opus' },
    { value: 'claude-3-haiku-20240307', label: 'Claude 3 Haiku' },
  ],
  Cursor: [
    { value: 'claude-opus-4-7-thinking-max', label: 'Claude 4.7 Opus (Thinking Max) (default)' },
    { value: 'claude-opus-4-7-thinking-high', label: 'Claude 4.7 Opus (Thinking High)' },
    { value: 'composer-1.5', label: 'Composer 1.5' },
    { value: 'composer-1', label: 'Composer 1' },
    { value: 'gpt-5.3-codex-low', label: 'GPT-5.3 Codex Low' },
    { value: 'gpt-5.3-codex-low-fast', label: 'GPT-5.3 Codex Low Fast' },
    { value: 'gpt-5.3-codex', label: 'GPT-5.3 Codex' },
    { value: 'gpt-5.3-codex-fast', label: 'GPT-5.3 Codex Fast' },
    { value: 'gpt-5.3-codex-high', label: 'GPT-5.3 Codex High' },
    { value: 'gpt-5.3-codex-high-fast', label: 'GPT-5.3 Codex High Fast' },
    { value: 'gpt-5.3-codex-xhigh', label: 'GPT-5.3 Codex Extra High' },
    { value: 'gpt-5.3-codex-xhigh-fast', label: 'GPT-5.3 Codex Extra High Fast' },
    { value: 'gpt-5.2', label: 'GPT-5.2' },
    { value: 'opus-4.6-thinking', label: 'Claude 4.6 Opus (Thinking)' },
    { value: 'sonnet-4.6', label: 'Claude 4.6 Sonnet' },
    { value: 'sonnet-4.6-thinking', label: 'Claude 4.6 Sonnet (Thinking)' },
    { value: 'opus-4.6', label: 'Claude 4.6 Opus' },
    { value: 'opus-4.5', label: 'Claude 4.5 Opus' },
    { value: 'opus-4.5-thinking', label: 'Claude 4.5 Opus (Thinking)' },
    { value: 'sonnet-4.5', label: 'Claude 4.5 Sonnet' },
    { value: 'sonnet-4.5-thinking', label: 'Claude 4.5 Sonnet (Thinking)' },
    { value: 'gemini-3.1-pro', label: 'Gemini 3.1 Pro' },
    { value: 'gemini-3-pro', label: 'Gemini 3 Pro' },
    { value: 'gemini-3-flash', label: 'Gemini 3 Flash' },
    { value: 'grok', label: 'Grok' },
  ],
};

const TYPE_OPTIONS = ['SF', 'Java', 'NodeJS', 'Swift', 'Python'];
const SOURCE_TYPE_OPTIONS = ['GitHub', 'GitLab', 'BitBucket', 'ZIP'];

const ProjectModal = ({ project, projects = [], onClose, onSave }) => {
  const [formData, setFormData] = useState({
    name: '', displayName: '', path: '', status: 'active', llm: '', projectType: '', promptFileName: '',
    scheduleDayOfWeek: '', sourceType: '', cliTool: '', repoSecret: '', portfolio: '', account: '', branch: '',
  });
  const [jiraFields, setJiraFields] = useState({
    baseUrl: '', apiToken: '', projectKey: '',
  });
  const [originalName, setOriginalName] = useState('');
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [zipFile, setZipFile] = useState(null);
  const [uploadingZip, setUploadingZip] = useState(false);
  const [zipUploadSuccess, setZipUploadSuccess] = useState(null);
  const fileInputRef = useRef(null);

  const portfolioOptions = useMemo(() => {
    const set = new Set();
    projects.forEach(p => { if (p.portfolio?.trim()) set.add(p.portfolio.trim()); });
    return Array.from(set).sort();
  }, [projects]);

  const accountOptions = useMemo(() => {
    if (!formData.portfolio) return [];
    const set = new Set();
    projects.forEach(p => { if (p.portfolio === formData.portfolio && p.account?.trim()) set.add(p.account.trim()); });
    return Array.from(set).sort();
  }, [projects, formData.portfolio]);

  useEffect(() => {
    if (project) {
      setOriginalName(project.name || '');
      setFormData({
        name: project.name || '', displayName: project.displayName || '',
        path: project.path || '', status: project.status || 'active',
        llm: project.llm || '', projectType: project.projectType || '', promptFileName: project.promptFileName || '',
        scheduleDayOfWeek: project.scheduleDayOfWeek || '', sourceType: project.sourceType || '',
        cliTool: project.cliTool || '', repoSecret: '', portfolio: project.portfolio || '',
        account: project.account || '', branch: project.branch || '',
      });
      if (project.jiraSecrets) {
        try {
          const parsed = JSON.parse(project.jiraSecrets);
          setJiraFields({
            baseUrl: parsed.baseUrl || '',
            apiToken: '',
            projectKey: parsed.projectKey || '',
          });
        } catch {
          setJiraFields({ baseUrl: '', apiToken: '', projectKey: '' });
        }
      }
    }
  }, [project]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => {
      const newData = { ...prev, [name]: value };
      if (name === 'cliTool') newData.llm = '';
      if (name === 'portfolio') newData.account = '';
      if (name === 'sourceType' && value !== 'ZIP') {
        setZipFile(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
      }
      return newData;
    });
  };

  const handleZipFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.name.endsWith('.zip') && file.type !== 'application/zip') {
        setError('Only ZIP files are allowed');
        e.target.value = '';
        return;
      }
      setZipFile(file);
      setError(null);
    }
  };

  const uploadZipFile = async (projectName) => {
    if (!zipFile) return true;
    const token = localStorage.getItem('authToken');
    if (!token) throw new Error('Not authenticated');
    const fd = new FormData();
    fd.append('file', zipFile);
    await axios.post(`${API_URL}/projects-config/${encodeURIComponent(projectName)}/upload-zip`, fd, {
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
    });
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!formData.name.trim()) { setError('Project name is required'); return; }
    if (!formData.cliTool) { setError('CLI Tool is required'); return; }
    if (!formData.llm) { setError('LLM is required'); return; }
    if (!formData.projectType) { setError('Project Type is required'); return; }
    if (!formData.sourceType) { setError('Source Type is required'); return; }
    if (!formData.promptFileName.trim()) { setError('Prompt File Name is required'); return; }

    setLoading(true);
    try {
      const dataToSave = { ...formData };
      if (project && !dataToSave.repoSecret) delete dataToSave.repoSecret;

      const hasAnyJiraField = jiraFields.baseUrl || jiraFields.apiToken || jiraFields.projectKey;
      if (hasAnyJiraField) {
        const secrets = { ...jiraFields };
        if (project && !secrets.apiToken) {
          try {
            const existing = JSON.parse(project.jiraSecrets || '{}');
            secrets.apiToken = existing.apiToken || '';
          } catch {}
        }
        dataToSave.jiraSecrets = JSON.stringify(secrets);
      } else if (!project) {
        dataToSave.jiraSecrets = '';
      }
      await onSave(dataToSave, originalName);
      if (zipFile && formData.sourceType === 'ZIP') {
        setUploadingZip(true);
        try {
          await uploadZipFile(dataToSave.name);
          setZipUploadSuccess('ZIP file uploaded successfully');
        } catch (zipErr) {
          setError(`Project saved, but ZIP upload failed: ${zipErr.response?.data?.message || zipErr.message}`);
          setLoading(false);
          setUploadingZip(false);
          return;
        }
        setUploadingZip(false);
      }
    } catch (err) {
      setError(err.message || 'Failed to save project');
      setLoading(false);
    }
  };

  const getAvailableModels = () => formData.cliTool ? (MODEL_OPTIONS[formData.cliTool] || []) : [];

  const inputClass = "bg-gray-50 border border-gray-300 text-gray-900 sm:text-sm rounded-lg focus:ring-primary-500 focus:border-primary-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-primary-500 dark:focus:border-primary-500";
  const labelClass = "block mb-2 text-sm font-medium text-gray-900 dark:text-white";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto overflow-x-hidden bg-gray-900/50 dark:bg-gray-900/80" onClick={onClose}>
      <div className="relative w-full max-w-2xl max-h-[90vh] p-4" onClick={(e) => e.stopPropagation()}>
        <div className="relative bg-white rounded-lg shadow dark:bg-gray-800 max-h-[85vh] flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b rounded-t dark:border-gray-600 flex-shrink-0">
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
              {project ? `Edit ${formData.displayName || formData.name || project.name}` : 'Add New Project'}
            </h3>
            <button onClick={onClose} className="text-gray-400 bg-transparent hover:bg-gray-200 hover:text-gray-900 rounded-lg text-sm w-8 h-8 inline-flex justify-center items-center dark:hover:bg-gray-600 dark:hover:text-white">
              <svg className="w-3 h-3" fill="none" viewBox="0 0 14 14"><path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="m1 1 6 6m0 0 6 6M7 7l6-6M7 7l-6 6" /></svg>
            </button>
          </div>

          {/* Body */}
          <form onSubmit={handleSubmit} className="flex flex-col flex-1 overflow-hidden">
            <div className="p-6 space-y-4 overflow-y-auto flex-1">
              {error && <div className="p-4 text-sm text-red-800 rounded-lg bg-red-50 dark:bg-gray-700 dark:text-red-400">{error}</div>}
              {zipUploadSuccess && <div className="p-4 text-sm text-green-800 rounded-lg bg-green-50 dark:bg-gray-700 dark:text-green-400">{zipUploadSuccess}</div>}

              <div>
                <label htmlFor="displayName" className={labelClass}>Display Name</label>
                <input type="text" id="displayName" name="displayName" value={formData.displayName} onChange={handleChange} placeholder="Human-readable project name (optional)" className={inputClass} autoComplete="off" />
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">Shown in the UI instead of the project name. Leave blank to use the project name.</p>
              </div>

              <div>
                <label htmlFor="name" className={labelClass}>Name *</label>
                <input type="text" id="name" name="name" value={formData.name} onChange={handleChange} placeholder="Project name" className={`${inputClass} ${project ? 'bg-gray-100 dark:bg-gray-600 cursor-not-allowed' : ''}`} readOnly={!!project} autoComplete="off" />
                {project && <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">Project name cannot be changed after creation</p>}
              </div>

              <div>
                <label className={labelClass}>Status</label>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input type="checkbox" checked={formData.status === 'active'} onChange={() => setFormData(prev => ({ ...prev, status: prev.status === 'active' ? 'inactive' : 'active' }))} className="sr-only peer" />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 dark:peer-focus:ring-primary-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-primary-600"></div>
                  <span className="ml-3 text-sm font-medium text-gray-900 dark:text-gray-300">{formData.status === 'active' ? 'Active' : 'Inactive'}</span>
                </label>
              </div>

              <div>
                <label htmlFor="path" className={labelClass}>Path</label>
                <input type="text" id="path" name="path" value={formData.path} onChange={handleChange} placeholder="https://github.com/org/repo" className={inputClass} autoComplete="off" />
              </div>

              <div>
                <label htmlFor="branch" className={labelClass}>Branch</label>
                <input type="text" id="branch" name="branch" value={formData.branch} onChange={handleChange} placeholder="default" className={inputClass} autoComplete="off" />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="cliTool" className={labelClass}>CLI Tool *</label>
                  <select id="cliTool" name="cliTool" value={formData.cliTool} onChange={handleChange} className={inputClass}>
                    <option value="">Select CLI Tool</option>
                    {CLI_TOOL_OPTIONS.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div>
                  <label htmlFor="llm" className={labelClass}>LLM *</label>
                  <select id="llm" name="llm" value={formData.llm} onChange={handleChange} disabled={!formData.cliTool} className={inputClass}>
                    <option value="">{formData.cliTool ? 'Select LLM' : 'Select CLI Tool first'}</option>
                    {getAvailableModels().map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="projectType" className={labelClass}>Project Type *</label>
                  <select id="projectType" name="projectType" value={formData.projectType} onChange={handleChange} className={inputClass}>
                    <option value="">Select Type</option>
                    {TYPE_OPTIONS.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div>
                  <label htmlFor="sourceType" className={labelClass}>Source Type *</label>
                  <select id="sourceType" name="sourceType" value={formData.sourceType} onChange={handleChange} className={inputClass}>
                    <option value="">Select Source</option>
                    {SOURCE_TYPE_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              </div>

              {formData.sourceType === 'ZIP' && (
                <div>
                  <label className={labelClass}>Upload ZIP File {project ? '(optional)' : ''}</label>
                  <div className="flex items-center justify-center w-full">
                    <label htmlFor="zipFile" className="flex flex-col items-center justify-center w-full h-32 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:hover:border-gray-500 dark:hover:bg-gray-600">
                      <div className="flex flex-col items-center justify-center pt-5 pb-6">
                        <svg className="w-8 h-8 mb-2 text-gray-500 dark:text-gray-400" fill="none" viewBox="0 0 20 16"><path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 13h3a3 3 0 0 0 0-6h-.025A5.56 5.56 0 0 0 16 6.5 5.5 5.5 0 0 0 5.207 5.021C5.137 5.017 5.071 5 5 5a4 4 0 0 0 0 8h2.167M10 15V6m0 0L8 8m2-2 2 2" /></svg>
                        <p className="mb-1 text-sm text-gray-500 dark:text-gray-400">
                          {zipFile ? <span className="font-semibold">{zipFile.name}</span> : <><span className="font-semibold">Click to upload</span> ZIP file</>}
                        </p>
                      </div>
                      <input id="zipFile" ref={fileInputRef} type="file" accept=".zip,application/zip" onChange={handleZipFileChange} className="hidden" />
                    </label>
                  </div>
                  {zipFile && (
                    <button type="button" onClick={() => { setZipFile(null); if (fileInputRef.current) fileInputRef.current.value = ''; }}
                      className="mt-2 text-sm text-red-600 hover:text-red-800 dark:text-red-400">Remove file</button>
                  )}
                </div>
              )}

              <div>
                <label htmlFor="promptFileName" className={labelClass}>Prompt File Name *</label>
                <input type="text" id="promptFileName" name="promptFileName" value={formData.promptFileName} onChange={handleChange} placeholder="Prompt file name" className={inputClass} autoComplete="off" />
              </div>

              <div>
                <label htmlFor="scheduleDayOfWeek" className={labelClass}>Schedule Day of Week</label>
                <select id="scheduleDayOfWeek" name="scheduleDayOfWeek" value={formData.scheduleDayOfWeek} onChange={handleChange} className={inputClass}>
                  <option value="">Not scheduled</option>
                  {['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'].map((d, i) => <option key={i} value={String(i)}>{d}</option>)}
                </select>
              </div>

              <div>
                <label htmlFor="repoSecret" className={labelClass}>Repo Secret {project ? '(leave empty to keep current)' : ''}</label>
                <input type="password" id="repoSecret" name="repoSecret" value={formData.repoSecret} onChange={handleChange} placeholder={project ? '••••••••' : 'Enter repo secret'} className={inputClass} autoComplete="new-password" />
              </div>

              {/* Jira Integration */}
              <div className="pt-2 border-t border-gray-200 dark:border-gray-600">
                <div className="flex items-center gap-2 mb-3">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5 text-blue-600 dark:text-blue-400">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm5 11h-4v4h-2v-4H7v-2h4V7h2v4h4v2z"/>
                  </svg>
                  <span className="text-sm font-semibold text-gray-900 dark:text-white">Jira Integration</span>
                  <span className="text-xs text-gray-500 dark:text-gray-400">(optional)</span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="jiraBaseUrl" className={labelClass}>Base URL</label>
                    <input type="text" id="jiraBaseUrl" value={jiraFields.baseUrl} onChange={(e) => setJiraFields(prev => ({ ...prev, baseUrl: e.target.value }))} placeholder="https://jira.company.com" className={inputClass} autoComplete="off" />
                  </div>
                  <div>
                    <label htmlFor="jiraProjectKey" className={labelClass}>Project Key</label>
                    <input type="text" id="jiraProjectKey" value={jiraFields.projectKey} onChange={(e) => setJiraFields(prev => ({ ...prev, projectKey: e.target.value }))} placeholder="PROJ" className={inputClass} autoComplete="off" />
                  </div>
                </div>
                <div className="mt-4">
                  <label htmlFor="jiraApiToken" className={labelClass}>Personal Access Token {project ? '(leave empty to keep current)' : ''}</label>
                  <input type="password" id="jiraApiToken" value={jiraFields.apiToken} onChange={(e) => setJiraFields(prev => ({ ...prev, apiToken: e.target.value }))} placeholder={project?.jiraSecrets ? '••••••••' : 'Jira Personal Access Token'} className={inputClass} autoComplete="new-password" />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="portfolio" className={labelClass}>Portfolio</label>
                  <select id="portfolio" name="portfolio" value={formData.portfolio} onChange={handleChange} className={`${inputClass} mb-2`}>
                    <option value="">Select Portfolio</option>
                    {portfolioOptions.map(p => <option key={p} value={p}>{p}</option>)}
                  </select>
                  <input type="text" name="portfolio" value={formData.portfolio} onChange={handleChange} placeholder="Or type new" className={inputClass} autoComplete="off" />
                </div>
                <div>
                  <label htmlFor="account" className={labelClass}>Account</label>
                  <select id="account" name="account" value={formData.account} onChange={handleChange} disabled={!formData.portfolio} className={`${inputClass} mb-2`}>
                    <option value="">{formData.portfolio ? 'Select Account' : 'Select Portfolio first'}</option>
                    {accountOptions.map(a => <option key={a} value={a}>{a}</option>)}
                  </select>
                  <input type="text" name="account" value={formData.account} onChange={handleChange} placeholder="Or type new" className={inputClass} autoComplete="off" disabled={!formData.portfolio} />
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center p-6 space-x-2 border-t border-gray-200 rounded-b dark:border-gray-600 flex-shrink-0">
              <button type="submit" disabled={loading || uploadingZip}
                className="text-white bg-primary-700 hover:bg-primary-800 focus:ring-4 focus:outline-none focus:ring-primary-300 font-medium rounded-lg text-sm px-5 py-2.5 dark:bg-primary-600 dark:hover:bg-primary-700 dark:focus:ring-primary-800 disabled:opacity-50">
                {uploadingZip ? 'Uploading ZIP...' : (loading ? 'Saving...' : (project ? 'Update Project' : 'Create Project'))}
              </button>
              <button type="button" onClick={onClose}
                className="text-gray-500 bg-white hover:bg-gray-100 focus:ring-4 focus:outline-none focus:ring-gray-200 rounded-lg border border-gray-200 text-sm font-medium px-5 py-2.5 hover:text-gray-900 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-500 dark:hover:text-white dark:hover:bg-gray-600 dark:focus:ring-gray-600">
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ProjectModal;
